import { useEffect, useState, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Clock, TrendingUp, TrendingDown, Star, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function Transactions() {
  const { user, updateBalance, API } = useContext(AppContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [ratingDialog, setRatingDialog] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions/my`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/complete`);
      toast.success('Marked as complete! Buyer will be notified to verify.');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setProcessing(null);
    }
  };

  const handleVerify = async (transactionId) => {
    setProcessing(transactionId);
    try {
      await axios.post(`${API}/transactions/${transactionId}/verify`);
      toast.success('Transaction verified! Tokens released from escrow.');
      await updateBalance();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    } finally {
      setProcessing(null);
    }
  };

  const handleRate = async () => {
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await axios.post(`${API}/transactions/${ratingDialog.id}/rate`, {
        rating,
        review: review || null
      });
      toast.success('Thank you for your rating!');
      setRatingDialog(null);
      setRating(0);
      setReview('');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>,
      accepted: <Badge className="bg-blue-100 text-blue-800">Accepted</Badge>,
      refused: <Badge className="bg-red-100 text-red-800">Refused</Badge>,
      awaiting_verification: <Badge className="bg-orange-100 text-orange-800">Awaiting Verification</Badge>,
      completed: <Badge className="bg-green-100 text-green-800">Completed</Badge>,
      cancelled: <Badge className="bg-gray-100 text-gray-800">Cancelled</Badge>,
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const canRate = (tx) => {
    if (tx.status !== 'completed') return false;
    const isProvider = tx.to_user_id === user?.id;
    const isRequester = tx.from_user_id === user?.id;
    return (isProvider && !tx.provider_rating) || (isRequester && !tx.requester_rating);
  };

  const canComplete = (tx) => {
    return tx.status === 'accepted' && tx.to_user_id === user?.id;
  };

  const canVerify = (tx) => {
    return tx.status === 'awaiting_verification' && tx.from_user_id === user?.id;
  };

  const getRatingDisplay = (tx) => {
    const isProvider = tx.to_user_id === user?.id;
    const myRating = isProvider ? tx.provider_rating : tx.requester_rating;
    const theirRating = isProvider ? tx.requester_rating : tx.provider_rating;
    
    return (
      <div className="flex gap-4 mt-2 text-sm">
        {myRating && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">You rated:</span>
            <div className="flex">
              {[...Array(myRating)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </div>
        )}
        {theirRating && (
          <div className="flex items-center gap-1">
            <span className="text-gray-500">They rated:</span>
            <div className="flex">
              {[...Array(theirRating)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading transactions...</div>;
  }

  return (
    <div className="space-y-6" data-testid="transactions">
      <div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Transaction History</h1>
        <p className="text-gray-600">View all your time token exchanges</p>
      </div>

      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle>All Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No transactions yet</p>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx, index) => {
                const isSent = tx.from_user === user?.username;
                return (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`transaction-item-${index}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-2 rounded-full ${
                        isSent ? 'bg-orange-100' : 'bg-green-100'
                      }`}>
                        {isSent ? (
                          <TrendingDown className="w-5 h-5 text-orange-600" />
                        ) : (
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800" data-testid={`transaction-title-${index}`}>{tx.service_title}</p>
                        <p className="text-sm text-gray-500">
                          {isSent ? `To: ${tx.to_user}` : `From: ${tx.from_user}`}
                        </p>
                        <p className="text-xs text-gray-400">{new Date(tx.timestamp).toLocaleString()}</p>
                        {getRatingDisplay(tx)}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <p className={`text-lg font-semibold ${
                          tx.status === 'completed' && isSent ? 'text-orange-600' : 
                          tx.status === 'completed' ? 'text-green-600' : 'text-gray-600'
                        }`} data-testid={`transaction-amount-${index}`}>
                          {tx.status === 'completed' ? (isSent ? '-' : '+') : ''}{tx.time_tokens} tokens
                        </p>
                        {getStatusBadge(tx.status)}
                      </div>
                      <div className="flex flex-col gap-2">
                        {canComplete(tx) && (
                          <Button 
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleComplete(tx.id)}
                            disabled={processing === tx.id}
                            data-testid={`complete-button-${index}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Mark Complete
                          </Button>
                        )}
                        {canVerify(tx) && (
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleVerify(tx.id)}
                            disabled={processing === tx.id}
                            data-testid={`verify-button-${index}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify & Complete
                          </Button>
                        )}
                        {canRate(tx) && (
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => setRatingDialog(tx)}
                            data-testid={`rate-button-${index}`}
                          >
                            <Star className="w-4 h-4 mr-1" />
                            Rate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rating Dialog */}
      {ratingDialog && (
        <Dialog open={!!ratingDialog} onOpenChange={() => setRatingDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rate Transaction</DialogTitle>
              <DialogDescription>
                Rate your experience with {ratingDialog.from_user_id === user?.id ? ratingDialog.to_user : ratingDialog.from_user}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="transition-colors"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Review (Optional)</label>
                <Textarea
                  placeholder="Share your experience..."
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  data-testid="review-input"
                />
              </div>
              <Button onClick={handleRate} className="w-full" data-testid="submit-rating">
                Submit Rating
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default Transactions;